<template>
    <div
        class="offer-item-tile"
        :class="{ 'not-available': !isAvailable }"
        @click="onClick"
        @touchstart="onTouchStart"
        @touchend="onTouchEnd"
    >
        <ItemFlags :articleId="item.articleId" />

        <div v-if="articleInCartQuantity > 0" class="quantity-badge">{{ articleInCartQuantity }}x</div>

        <div v-if="imagePath" class="image" :class="{ 'operator-offer-item-image': operatorImagePath }">
            <img loading="lazy" decoding="async" :src="imagePath" alt="" />
        </div>
        <div class="item-name-with-price" :class="{ 'has-image': imagePath }">
            <span
                :class="{ 'negative-price': props.item.hasNegativePrice, 'kiosk-mode': kiosk.isKioskMode }"
                class="item-name"
                >{{ itemName }}<ItemIcons :articleId="item.articleId" includeOrderedBefore />
            </span>

            <span
                v-if="price && (kiosk.isKioskMode || displayMainItemPrices)"
                class="item-price"
                :class="{ 'kiosk-mode': kiosk.isKioskMode }"
                >{{ price }}</span
            >
        </div>
        <i v-if="!isAvailable" class="fas fa-ban"></i>
        <div class="item-color" :class="{ 'negative-price': props.item.hasNegativePrice }"></div>

        <div v-if="canBeAddedWithoutUserSelection" class="open-popover-btn" @click.stop.prevent="onOpenPopoverClick">
            <i v-if="kiosk.isKioskMode" class="fas fa-info"></i>
            <template v-else>&plus;</template>
        </div>

        <div
            v-if="isTimerItem"
            class="timer-btn"
            :class="{ running: isTimerRunning, disabled: isStartBlocked }"
            @click.stop.prevent="onTimerClick">
            <i class="fas" :class="isTimerRunning ? 'fa-stop' : 'fa-play'"></i>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useKioskStore } from "@/app/stores/kiosk";
    import { useTapRecognizer } from "@/components/composables/useTapRecognizer";
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import type { ICustomerArticleWithOptions } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { useOfferStore } from "@/data/stores/offer";
    import { useTimersStore } from "@/data/stores/timers";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { exceptionTypes } from "@/data/util/Config";
    import { getSitePrice } from "@/shared/helpers/ItemPriceHelper";
    import { translate } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoney, toRate } from "@/shared/utils/format";
    import { computed, onMounted } from "vue";

    import { Popup } from "@/components/dialog/Popup";
    import ItemFlags from "@/components/order/ItemFlags.vue";
    import ItemIcons from "@/components/order/ItemIcons.vue";

    const cart = useCartStore();
    const offer = useOfferStore();
    const kiosk = useKioskStore();
    const timers = useTimersStore();

    // define properties
    const props = defineProps<{
        item: ICustomerArticleWithOptions;
        isAvailable: boolean;
    }>();

    const emit = defineEmits<{
        addToCartPopoverClick: [item: ICustomerArticleWithOptions];
        quickAddClick: [item: ICustomerArticleWithOptions];
    }>();

    // the site has to come from the offer store: this tile is rendered by the operator too,
    // where the app context Helper.formatPrice reads is never initialised, so it fell back to
    // the on site price and take away items showed their in house price. a price per minute is a rate with up to 5 decimals,
    // shown as such here - on the operator overview and in the kiosk alike
    const price = computed(() => {
        const sitePrice = getSitePrice(props.item, offer.orderService);
        return props.item.isPricePerMinute ? toRate(sitePrice) : toMoney(sitePrice);
    });
    const displayMainItemPrices = computed(() => {
        return offer.orderService?.useCase?.operatorDisplayMainItemPricesOnOfferOverview === true;
    });

    // outside the kiosk this tile is only rendered by the two operator offer overviews, so the
    // operator setting alone decides here - like in the operator add to cart popover. the tiles
    // are a fraction of the size of the kiosk ones, so they show the 148x148 thumbnail and not
    // the raw original
    // outside the kiosk this tile only serves operators (the Operator App and the Mobile POS), who get the
    // rule of the internal frontends. the kiosk guest gets the name in the chosen language, never the internal one
    const itemName = computed(() =>
        kiosk.isKioskMode ? translate(props.item.name) : getNameInLocationLanguage(props.item, true)
    );

    const operatorImagePath = computed(() => {
        if (kiosk.isKioskMode) return undefined;
        if (offer.orderService?.useCase?.operatorDisplayItemImages !== true) return undefined;

        return props.item.imagePath;
    });
    // the kiosk tile is 155px * $kioskScale, so it uses the 700x700 kiosk variant rather than the
    // 148x148 thumbnail. the endpoint falls back to the thumbnail for images uploaded before the
    // kiosk variant existed, the ?? only covers an offer payload cached from before this change
    const imagePath = computed(() =>
        kiosk.isKioskMode ? (props.item.kioskImagePath ?? props.item.imagePath) : operatorImagePath.value
    );

    const articleInCartQuantity = computed(() => cart.getCartItemQuantity(props.item.articleId));
    const maxQuantity = computed(() => {
        const max = props.item.maxQuantity ?? Number.MAX_VALUE;
        return Math.max(0, max - articleInCartQuantity.value);
    });

    const isOpenPriceValid = computed(() => !props.item.isOpenPriceEnabled);

    // the popover hides an option group that came back without options, so such a group does
    // not count as an option here either
    const hasAvailableOptions = computed(() => props.item.options.some((group) => group.options.length > 0));

    // on the kiosk an item with options always opens the popover: it is never added to the cart
    // by the tile itself and it carries no button, the "i" button belongs to the items without
    // options. outside the kiosk the plus button and the quick add stay as they were
    // a price per minute item is a timer item on the mobile POS: it is not added to the cart, a tap on it orders it with
    // pay later and starts its timer. the kiosk keeps the tile as it was
    const isTimerItem = computed(() => props.item.isPricePerMinute && !kiosk.isKioskMode);
    const isTimerRunning = computed(() => timers.isRunning(props.item.articleId));
    // a timer that is not running can not be started while a payment is being made
    const isStartBlocked = computed(() => !isTimerRunning.value && timers.isStartBlocked);

    onMounted(() => {
        if (isTimerItem.value) timers.load();
    });

    // a price per minute item has no plus button, wherever it is displayed: it can not be added with the popover
    const canBeAddedWithoutUserSelection = computed(
        () =>
            !props.item.isPricePerMinute &&
            isOpenPriceValid.value &&
            maxQuantity.value > 0 &&
            props.item.canBeAddedWithoutUserSelection &&
            !(kiosk.isKioskMode && hasAvailableOptions.value)
    );

    let suppressClick = false;

    const { onTouchStart, onTouchEnd } = useTapRecognizer({
        onTap: (_, e) => {
            const className = (e.target as any)?.className;
            const isButton = className?.includes("open-popover-btn") || className?.includes("timer-btn");
            if (!isButton) {
                suppressClick = true;
                setTimeout(() => {
                    suppressClick = false;
                }, 350);
                e.preventDefault();
                onClickItem();
            }
        },
        toleranceX: 40,
        toleranceY: 20,
        shouldPreventDefault: false,
    });

    function onClick() {
        if (suppressClick) {
            suppressClick = false;
            return;
        }

        onClickItem();
    }

    function onClickItem() {
        // the whole tile of a timer item starts / stops its timer, the play / stop button only shows which one it does
        if (isTimerItem.value) {
            onTimerClick();
            return;
        }

        if (canBeAddedWithoutUserSelection.value) {
            emit("quickAddClick", props.item);
        } else {
            emit("addToCartPopoverClick", props.item);
        }
    }

    function onOpenPopoverClick() {
        emit("addToCartPopoverClick", props.item);
    }

    let timerActionInProgress = false;

    // the play button orders the item and starts its timer, the stop button stops the running one. the server records
    // the operator who stopped it
    async function onTimerClick() {
        if (timerActionInProgress || isStartBlocked.value || offer.orderService == null) return;
        timerActionInProgress = true;

        const isStop = isTimerRunning.value;
        try {
            if (isStop) {
                await timers.stop({ articleId: props.item.articleId });
            } else {
                await timers.start({
                    articleId: props.item.articleId,
                    menuCategoryId: props.item.categoryId,
                    offerId: props.item.offerId,
                    orderServiceId: offer.orderService.id,
                });
            }
        } catch (ex) {
            logError(ex);

            const reason = (ex as DaoError)?.additionalData?.reason;
            Popup.show({
                message: isStop
                    ? translations.offerSection.timerStopFailed
                    : reason === exceptionTypes.timerIsAlreadyRunningException
                      ? translations.offerSection.timerAlreadyRunning
                      : translations.offerSection.timerStartFailed,
            });

            // the timer was started meanwhile, possibly on another bill of the table: the tile shows it running again
            if (reason === exceptionTypes.timerIsAlreadyRunningException) timers.load();
        } finally {
            timerActionInProgress = false;
        }
    }
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .offer-item-tile {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100px;
        padding: 0.25em;
        background: white;
        border: 1px solid #dcdcdc;
        border-radius: 0.25em;
        color: colors.$fontColor;
        font-size: 18px;
        cursor: pointer;
        transition: 0.2s linear;

        @include mixins.hover-and-active() {
            color: colors.$fontColorHover;
            background-color: #ddd;
            border-color: #bbb;
        }

        // the kiosk grid gives the tile a smaller font size than the app one, so nothing
        // that is meant to grow on the kiosk can be left to follow it
        .kiosk-mode & {
            min-height: 100px * variables.$kioskScale;
        }

        > .image {
            overflow: hidden;
            margin-top: 2em;

            > img {
                aspect-ratio: 1 / 1;
                width: 150px;
                height: 150px;
                object-fit: cover;
                border-radius: 0.25em;

                .kiosk-mode & {
                    width: 155px * variables.$kioskScale;
                    height: 155px * variables.$kioskScale;
                }
            }

            .kiosk-mode & {
                margin-top: 0.75em;
                padding: 0 0.75em;
            }

            // the operator tile is much smaller than the kiosk one: the image sits right under
            // the top edge and the name keeps its bottom margin, so it stays clear of the
            // open-popover button. on the mobile POS the tile is only ~120px wide, so the image
            // follows the tile width instead of a fixed size, capped at the 150px it used to be
            // so the 148x148 thumbnail is never upscaled on the wider tablet tiles
            &.operator-offer-item-image {
                margin-top: 0.375em;
                width: 100%;
                max-width: 150px;

                > img {
                    width: 100%;
                    height: 100%;
                }

                //+ .item-name-with-price {
                //    margin: 0.375em 0 2.25em;
                //}
            }
        }

        > .item-name-with-price {
            margin: 1em 0.25em;
            text-align: center;
            display: flex;
            flex-direction: column;

            &.has-image {
                margin: 0.375em 0.25em 1em 0.25em;
            }

            .kiosk-mode & {
                margin: 0.75em 0;
            }

            > .item-name {
                font-weight: 600;
                color: colors.$fontColor;

                word-break: break-word;

                &.negative-price {
                    color: colors.$red !important;
                }

                // the kiosk grid overrides the tile with a smaller font size, so the name
                // is pinned to the app size of the tile at the kiosk scale
                &.kiosk-mode {
                    font-size: 18px * variables.$kioskScale;
                }
            }

            // only rendered on the kiosk, where the grid sets the tile to 15px
            > .item-price {
                font-size: 16px;
                font-weight: 400;
                margin-top: 0.225em;
                color: colors.$fontColor;
                text-align: center;

                // the kiosk grid overrides the tile with a smaller font size, so the name
                // is pinned to the app size of the tile at the kiosk scale
                &.kiosk-mode {
                    font-size: 15px * variables.$kioskScale;
                    margin-top: 0.125em;
                }
            }
        }

        .item-color {
            position: absolute;
            bottom: 0;
            height: 5px;
            left: 0;
            right: 0;
            background-color: #777;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;

            &.negative-price {
                background-color: colors.$red !important;
            }

            .kiosk-mode & {
                height: 5px * variables.$kioskScale;
                border-bottom-left-radius: 4px * variables.$kioskScale;
                border-bottom-right-radius: 4px * variables.$kioskScale;
            }
        }

        &.not-available {
            cursor: default;
            background-color: #f7f7f7 !important;
            border-color: #dcdcdc !important;

            .item-name {
                color: #bbb;
            }

            i {
                position: absolute;
                top: 0.5em;
                right: 0.5em;
                color: #ccc !important;

                // the glyph has no size of its own, it follows the tile font size
                .kiosk-mode & {
                    font-size: 18px * variables.$kioskScale;
                }
            }

            .item-color {
                display: none;
            }
        }

        .quantity-badge {
            position: absolute;
            top: 0.25em;
            left: 0.5em;
            font-size: 18px;
            font-weight: 600;
            // pinned, so it never takes a row of its own on a tile that carries no flags
            line-height: 1.2;
            color: var(--colorPrimary);

            .kiosk-mode & {
                font-size: 20px * variables.$kioskScale;
            }
        }

        // the flags open the tile: they take the first row inside the frame, above the badge
        // and above the image and the name, and wrap onto a second row only once they reach
        // the right edge of the tile. they are indented to the quantity badge, which hangs on
        // the tile's padding box and carries its own font size, so the row repeats the badge's
        // 0.5em indent in that font size (18px in the app, 20px * $kioskScale on the kiosk)
        // and gives back the tile padding that an in-flow row starts inside of
        .item-flags {
            align-self: stretch;
            margin: 0 0 auto calc(0.5 * 18px - 0.25em);

            // the badge below carries the auto margin for the pair, so the row keeps none of
            // its own and the two stay together. dropping this rule only lets the free space
            // back in between them, so a browser without :has() still never overlaps the two
            &:has(+ .quantity-badge) {
                margin-bottom: 0;
            }

            .kiosk-mode & {
                margin-left: calc(0.5 * 20px * variables.$kioskScale - 0.25em);
            }
        }

        // with the flags on the first row the badge drops under them, and it joins the flow to
        // get there: the row wraps, so how far the badge has to come down follows the number of
        // rows and cannot be written as an offset. it keeps the indent of the row above it
        .item-flags + .quantity-badge {
            position: static;
            align-self: flex-start;
            // the top margin clears the 0.3em fold that hangs below the first flag, which is
            // 0.3em of the flag font size (14px in _item-flags.scss), not of the badge's own
            margin: (0.3 * 14px) 0 auto calc(0.5 * 18px - 0.25em);

            .kiosk-mode & {
                margin-top: 0.3 * 14px * variables.$kioskScale;
                margin-left: calc(0.5 * 20px * variables.$kioskScale - 0.25em);
            }
        }

        // the tile centres its content, which would pull the flags off the top edge on a tile
        // that the row stretched. the two auto margins take that free space above and below
        // the image and the name instead, so the flags stay on the first row and the rest
        // stays centred in what is left under them
        .item-flags ~ .item-name-with-price {
            margin-bottom: auto;
        }

        // the play / stop button of a timer item sits at the bottom left, where the plus button is at the right
        .timer-btn {
            position: absolute;
            bottom: 5px;
            left: -1px;
            width: 50px;
            height: 35px;
            box-sizing: border-box;
            border: 1px solid #dedede;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            background-color: rgba(255, 255, 255, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            // the start icon of a timer item that is not running, i.e. never started or stopped
            color: colors.$green;
            transition: 0.2s linear;

            @include mixins.hover-and-active() {
                background-color: rgba(255, 255, 255, 1);
            }

            &.running {
                color: colors.$orange;
            }

            &.disabled {
                opacity: 0.4;
                pointer-events: none;
            }

            // the tap recognizer tells the button from the tile by the class of the touch target
            > i {
                pointer-events: none;
            }
        }

        .open-popover-btn {
            position: absolute;
            bottom: 5px;
            right: -1px;
            width: 50px;
            height: 35px;
            box-sizing: border-box;
            border: 1px solid #dedede;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            background-color: rgba(255, 255, 255, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            font-weight: 600;
            transition: 0.2s linear;

            @include mixins.hover-and-active() {
                background-color: rgba(255, 255, 255, 1);
            }

            // the tap recognizer tells the button from the tile by the class of the touch
            // target, so the glyph must not become that target
            > i {
                pointer-events: none;
            }

            .kiosk-mode & {
                bottom: 5px * variables.$kioskScale;
                width: 50px * variables.$kioskScale;
                height: 35px * variables.$kioskScale;
                border-top-left-radius: 5px * variables.$kioskScale;
                border-top-right-radius: 5px * variables.$kioskScale;
                font-size: 24px * variables.$kioskScale;

                i.fas.fa-info {
                    font-size: 16px * variables.$kioskScale;
                }
            }
        }

        @include mixins.hover-and-active() {
            > .open-popover-btn {
                border-color: colors.$light-gray-500;
            }
        }
    }
</style>
